import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "@/api/notifications";
import type {
  NotificationChannel,
  NotificationEvent,
  PreferenceItem,
  PreferenceOut,
} from "@/api/schemas";
import { Button } from "@/components/ui/button";

const CHANNELS: { id: NotificationChannel; label: string; available: boolean; note?: string }[] = [
  { id: "websocket", label: "In-app", available: true },
  { id: "email", label: "Email", available: false, note: "SES integration coming soon" },
  { id: "slack", label: "Slack", available: false, note: "Connect Slack first (coming soon)" },
  { id: "push", label: "Push", available: false, note: "Mobile apps not in v1" },
];

const EVENTS: { id: NotificationEvent; label: string; description: string }[] = [
  {
    id: "ticket_assigned",
    label: "Ticket assigned to me",
    description: "Whenever a ticket is reassigned to you, manually or by an automation rule.",
  },
  {
    id: "ticket_mentioned",
    label: "Mentioned in a note",
    description: "When a teammate @mentions you in an internal note.",
  },
  {
    id: "ticket_reply_received",
    label: "Reply received",
    description: "A new inbound message landed on a ticket assigned to you.",
  },
  {
    id: "ticket_sla_warning",
    label: "SLA warning",
    description: "An assigned ticket has used 80% of its SLA window without a response.",
  },
  {
    id: "ticket_sla_breached",
    label: "SLA breached",
    description: "An assigned ticket has missed its SLA — first response or resolution.",
  },
];

const PREFS_KEY = ["notification-preferences"] as const;

type PrefMap = Record<string, boolean>;

function key(channel: NotificationChannel, event: NotificationEvent): string {
  return `${channel}:${event}`;
}

function buildMap(items: PreferenceOut[]): PrefMap {
  const out: PrefMap = {};
  for (const item of items) {
    out[`${item.channel}:${item.event_type}`] = item.enabled;
  }
  return out;
}

function diff(initial: PrefMap, current: PrefMap): PreferenceItem[] {
  const changed: PreferenceItem[] = [];
  for (const k of Object.keys(current)) {
    const next = current[k];
    if (next === undefined) continue;
    if (initial[k] !== next) {
      const [channel, event_type] = k.split(":") as [NotificationChannel, NotificationEvent];
      changed.push({ channel, event_type, enabled: next });
    }
  }
  return changed;
}

function NotificationsSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: PREFS_KEY,
    queryFn: getNotificationPreferences,
  });

  const initialMap = useMemo<PrefMap>(
    () => (data ? buildMap(data.preferences) : {}),
    [data],
  );

  const [draft, setDraft] = useState<PrefMap>({});
  useEffect(() => {
    setDraft(initialMap);
  }, [initialMap]);

  const dirty = useMemo(() => diff(initialMap, draft), [initialMap, draft]);

  const mutation = useMutation({
    mutationFn: (changes: PreferenceItem[]) => updateNotificationPreferences(changes),
    onSuccess: (resp) => {
      toast.success("Preferences saved");
      qc.setQueryData(PREFS_KEY, resp);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not save preferences");
    },
  });

  function setEnabled(channel: NotificationChannel, event: NotificationEvent, enabled: boolean) {
    setDraft((prev) => ({ ...prev, [key(channel, event)]: enabled }));
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-medium tracking-tight">Notifications</h1>
        <p className="mt-1 text-sm text-text-muted">
          Choose which events trigger notifications, and through which channels.
          In-app notifications appear in the bell at the top right; other channels
          are coming soon.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-subtle text-left">
                  <th className="px-4 py-3 font-medium">Event</th>
                  {CHANNELS.map((c) => (
                    <th
                      key={c.id}
                      className="w-28 px-3 py-3 text-center font-medium"
                      title={c.note ?? ""}
                    >
                      {c.label}
                      {!c.available && (
                        <div className="text-[10px] font-normal text-text-muted">
                          (soon)
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EVENTS.map((evt) => (
                  <tr key={evt.id} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-text">{evt.label}</div>
                      <div className="mt-0.5 text-xs text-text-muted">{evt.description}</div>
                    </td>
                    {CHANNELS.map((c) => {
                      const k = key(c.id, evt.id);
                      const checked = draft[k] ?? true;
                      const disabled = !c.available;
                      return (
                        <td key={c.id} className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => setEnabled(c.id, evt.id, e.target.checked)}
                            aria-label={`${c.label} for ${evt.label}`}
                            className="h-4 w-4 cursor-pointer accent-accent disabled:cursor-not-allowed disabled:opacity-40"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
            <span className="text-xs text-text-muted">
              {dirty.length === 0
                ? "No changes"
                : `${dirty.length} change${dirty.length === 1 ? "" : "s"} pending`}
            </span>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDraft(initialMap)}
              disabled={dirty.length === 0 || mutation.isPending}
            >
              Reset
            </Button>
            <Button
              type="button"
              onClick={() => mutation.mutate(dirty)}
              disabled={dirty.length === 0 || mutation.isPending}
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/_auth/settings/notifications")({
  component: NotificationsSettingsPage,
});
